import React from "react";
import { Link } from "react-router-dom";
import { PageSeo } from "@/components/seo/PageSeo";
import { LegalPage } from "@/components/layout/LegalPage";

/**
 * Frac Terms & Conditions — 21-section client-provided body.
 * Wording preserved verbatim except for the KL office address in Section 21,
 * which uses the sitewide-corrected address (KL Eco City, 59200) rather than
 * the pre-correction paste that had been superseded earlier this session.
 */

const LINK_CLS =
  "font-semibold text-frac-purple underline underline-offset-2 decoration-frac-purple/40 hover:text-frac-violet hover:decoration-frac-violet transition-colors duration-fast ease-out";

function PrivacyLink() {
  return (
    <Link to="/privacy" className={LINK_CLS} data-testid="terms-privacy-link">
      Privacy Policy
    </Link>
  );
}

// Section 20 sub-clause — label + paragraph, semantic h3.
function GeneralClause({ label, children }) {
  return (
    <div>
      <h3 className="text-base font-semibold text-frac-purple">{label}.</h3>
      <p className="mt-1 text-frac-gray-70">{children}</p>
    </div>
  );
}

const sections = [
  {
    id: "about-these-terms",
    heading: "About these Terms",
    tocLabel: "About",
    body: (
      <>
        <p>
          These Terms &amp; Conditions ("Terms") govern your access to and
          use of the website at www.frac.io and its subdomains, together
          with all content, pages, documents and features made available on
          it (the "Site").
        </p>
        <p>
          The Site is operated by Frac Pte. Ltd. (UEN 202227114W), a
          private company limited by shares incorporated in Singapore,
          together with its subsidiaries and affiliates, including Frac
          Sdn. Bhd. in Malaysia ("Frac", "we", "us" or "our").
        </p>
        <p>
          These Terms should be read together with our <PrivacyLink />,
          which is incorporated into and forms part of these Terms.
        </p>
      </>
    ),
  },
  {
    id: "acceptance",
    heading: "Acceptance",
    tocLabel: "Acceptance",
    body: (
      <>
        <p>
          By accessing, browsing or otherwise using the Site, you confirm
          that you have read, understood and agree to be bound by these
          Terms. If you do not agree to any part of these Terms, please do
          not use the Site.
        </p>
        <p>
          The Site does not offer user accounts, sign-in or authenticated
          areas. Your agreement to these Terms arises from your use of the
          Site itself, and from any submission you choose to make through
          the enquiry or subscription forms.
        </p>
      </>
    ),
  },
  {
    id: "who-may-use-the-site",
    heading: "Who may use the Site",
    tocLabel: "Who may use",
    body: (
      <>
        <p>
          The Site is intended for business and professional audiences. By
          using it, you represent and warrant that:
        </p>
        <ul className="ml-6 list-disc space-y-2">
          <li>
            you are of legal age in your jurisdiction and have capacity to
            enter into a binding agreement;
          </li>
          <li>
            where you use the Site on behalf of an organization, you have
            authority to bind that organization to these Terms, and "you"
            refers to both you and that organization;
          </li>
          <li>
            your use of the Site complies with all laws and regulations
            applicable to you; and
          </li>
          <li>
            neither you nor any organization you represent is named on a
            sanctions or restricted-party list maintained by the United
            Nations Security Council, the Office of Foreign Assets Control
            of the U.S. Department of the Treasury, the U.S. Department of
            Commerce, the European Union, the United Kingdom, Singapore or
            Malaysia.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "changes-to-these-terms",
    heading: "Changes to these Terms",
    tocLabel: "Changes",
    body: (
      <p>
        We may amend these Terms from time to time. Where we do, we will
        update the effective date at the top of this page. Material changes
        will be indicated as such. Your continued use of the Site after an
        amendment takes effect constitutes acceptance of the amended Terms.
        Please review this page periodically.
      </p>
    ),
  },
  {
    id: "the-site-is-informational",
    heading: "The Site is informational",
    tocLabel: "Informational only",
    body: (
      <>
        <p>
          The Site is a corporate and marketing website. Its purpose is to
          describe Frac, our technology and our capabilities.
        </p>
        <p>
          Nothing on the Site constitutes an offer, solicitation,
          recommendation or advice of any kind — including financial,
          legal, tax, accounting, regulatory or other professional advice —
          and nothing on the Site should be relied upon as such. You should
          obtain independent professional advice appropriate to your
          circumstances before acting on anything described here.
        </p>
        <p>
          Descriptions of our platform, products, capabilities and roadmap
          are provided for general information. They do not form part of
          any contract, do not constitute a representation as to fitness
          for any particular purpose, and are subject to change without
          notice.
        </p>
      </>
    ),
  },
  {
    id: "these-terms-do-not-govern-our-services",
    heading: "These Terms do not govern our services",
    tocLabel: "Not services",
    body: (
      <>
        <p>
          Access to and use of any Frac platform, product or service is
          governed exclusively by a separate written agreement executed
          between Frac and the relevant client — typically a master
          services agreement, order form, licence agreement or statement of
          work.
        </p>
        <p>
          Where these Terms conflict with such an agreement, the executed
          agreement prevails in respect of the services it covers. Use of
          the Site creates no entitlement to any Frac product or service,
          and no commercial relationship arises until a written agreement
          is signed by both parties.
        </p>
      </>
    ),
  },
  {
    id: "forward-looking-statements",
    heading: "Forward-looking statements",
    tocLabel: "Forward-looking",
    body: (
      <>
        <p>
          Parts of the Site describe planned capabilities, product
          direction and areas of ongoing research and development.
          Statements of this kind are forward-looking. They reflect our
          expectations as at the date of publication and are subject to
          technical, commercial, operational and regulatory factors, many
          of which are outside our control.
        </p>
        <p>
          Planned capabilities may be modified, delayed or discontinued.
          Any illustration of an interface, dashboard or terminal shown on
          the Site is a design illustration only and does not depict live
          data, actual client information or real market figures. We
          undertake no obligation to update forward-looking statements.
        </p>
      </>
    ),
  },
  {
    id: "content-on-the-site",
    heading: "Content on the Site",
    tocLabel: "Content",
    body: (
      <>
        <p>
          All content featured or displayed on the Site — including text,
          graphics, photographs, illustrations, video, diagrams, data,
          software, page layouts and the selection, coordination and
          arrangement of that content (together, the "Content") — is owned
          by Frac, our licensors or other providers of such material
          (together, our "Content Providers"), and is protected by
          copyright, patent, design, confidential information and other
          intellectual property laws.
        </p>
        <p>All rights not expressly granted are reserved.</p>
      </>
    ),
  },
  {
    id: "permitted-use-of-the-content",
    heading: "Permitted use of the Content",
    tocLabel: "Permitted use",
    body: (
      <>
        <p>
          We grant you a limited, personal, non-exclusive, non-transferable
          and revocable licence to access and view the Content for your
          own internal business or personal reference.
        </p>
        <p>
          Except as expressly permitted by applicable law or by these
          Terms, you may not reproduce, distribute, publish, modify, adapt,
          frame, incorporate into another website, store, transmit or
          create derivative works from any Content without our prior
          written permission and, where relevant, that of our Content
          Providers. The Content may not be sold, licensed or used for any
          commercial purpose without that permission.
        </p>
        <p>
          You may quote short extracts from the Site for journalistic,
          academic or review purposes, provided the extract is accurate, is
          attributed to Frac, is not misleading as to context, and does not
          suggest endorsement.
        </p>
      </>
    ),
  },
  {
    id: "our-brand",
    heading: "Our brand",
    tocLabel: "Brand",
    body: (
      <>
        <p>
          The Frac name, logo, wordmark, product names, taglines, design
          system and other brand identifiers appearing on the Site (the
          "Marks") are the property of Frac, whether registered or
          unregistered.
        </p>
        <p>
          The Marks may not be used publicly without our express written
          permission, and may not be used in any manner likely to cause
          confusion, or in any manner that disparages or discredits Frac.
        </p>
        <p>
          Other product, service and company names referenced on the Site
          may be marks of their respective owners and are the exclusive
          property of those owners. Their appearance on the Site does not
          imply any endorsement of Frac by those owners, or of those owners
          by Frac.
        </p>
      </>
    ),
  },
  {
    id: "information-you-submit",
    heading: "Information you submit",
    tocLabel: "Submissions",
    body: (
      <>
        <p>
          The Site provides forms through which you may contact us or
          subscribe to our updates.
        </p>
        <p>
          By submitting information through the Site, you confirm that the
          information you provide is accurate, that you are entitled to
          provide it, and that where it relates to another person you have
          the necessary authority to do so.
        </p>
        <p>
          Please do not submit confidential, proprietary or sensitive
          information through the Site's forms. Information sent through
          an unsolicited enquiry is not treated as confidential and is not
          subject to any obligation of confidence unless and until a
          written confidentiality agreement is in place between us.
        </p>
        <p>
          If you send us feedback, suggestions, ideas or comments about
          the Site or our products, you grant Frac a perpetual,
          irrevocable, worldwide, royalty-free and sublicensable licence to
          use, reproduce, modify and incorporate that material for any
          purpose, without obligation or attribution to you.
        </p>
        <p>
          Our handling of personal data is described in our{" "}
          <PrivacyLink />. Every subscription message we send includes an
          unsubscribe mechanism.
        </p>
      </>
    ),
  },
  {
    id: "acceptable-use",
    heading: "Acceptable use",
    tocLabel: "Acceptable use",
    body: (
      <>
        <p>You agree not to:</p>
        <ul className="ml-6 list-disc space-y-2">
          <li>
            use the Site in any way that breaches any applicable law or
            regulation;
          </li>
          <li>
            duplicate, mirror or systematically copy any part of the Site
            or the Content;
          </li>
          <li>
            redistribute, resell, sublicense, lease, transfer, time-share
            or otherwise make available to any third party the Content or
            any of your rights under these Terms;
          </li>
          <li>
            use the Site or the Content for any public display, public
            performance, sale, rental or commercial exploitation;
          </li>
          <li>
            engage in any conduct that disables, overburdens, damages,
            impairs, restricts or interferes with the Site, or with anyone
            else's use of it;
          </li>
          <li>
            introduce any virus, worm, logic bomb, trojan horse or other
            material that is malicious or technologically harmful;
          </li>
          <li>
            attempt to gain unauthorized access to, probe, scan or test
            the vulnerability of the Site, its servers or any connected
            system, or circumvent any authentication, encryption or
            security measure;
          </li>
          <li>
            use any data mining tool, bot, scraper, crawler or similar
            automated means to extract data from the Site, except for a
            search engine crawler operating in accordance with our
            published robots.txt;
          </li>
          <li>
            place pop-up windows over pages of the Site, inject content
            into them, or otherwise alter how the Site is displayed;
          </li>
          <li>
            remove, obscure or falsify any copyright notice, attribution,
            legal notice or proprietary designation appearing on the Site
            or in any Content;
          </li>
          <li>
            frame the Site, or apply framing techniques to any Content,
            without our written permission;
          </li>
          <li>
            impersonate any person or entity, or misrepresent your
            affiliation with any person or entity;
          </li>
          <li>
            transmit unsolicited or unauthorized advertising, promotional
            material, junk mail, spam, chain letters or any other form of
            solicitation through any facility on the Site;
          </li>
          <li>
            harass, stalk or harm any other person, or collect or store
            personal data about other visitors to the Site; or
          </li>
          <li>
            use the Content or any information obtained through the Site
            in a manner that infringes the copyright, patent, design,
            confidential information, publicity or other proprietary
            rights of any party.
          </li>
        </ul>
        <p>
          We may suspend or terminate your access to the Site, in whole or
          in part, at any time and without prior notice, where we consider
          it necessary to protect the Site, our users or Frac. We will not
          be liable to you or to any third party for doing so.
        </p>
      </>
    ),
  },
  {
    id: "links-to-other-resources",
    heading: "Links to other resources",
    tocLabel: "Links",
    body: (
      <>
        <p>
          The Site may contain links to third-party websites, resources
          and documents. These are provided for convenience only and do not
          constitute an endorsement.
        </p>
        <p>
          We have no control over the content of those sites or resources,
          and accept no responsibility for them or for any loss or damage
          arising from your use of them. If you access a third-party site,
          you do so entirely at your own risk and subject to that site's
          own terms and privacy policy. We may remove or change any link at
          any time.
        </p>
      </>
    ),
  },
  {
    id: "availability-of-the-site",
    heading: "Availability of the Site",
    tocLabel: "Availability",
    body: (
      <>
        <p>
          Electronic and computer-based systems are inherently vulnerable
          to disruption, delay and failure, and may become unavailable as a
          result of both foreseeable and unforeseeable events.
        </p>
        <p>
          We do not warrant that the Site will be available at any
          particular time, will be uninterrupted, or will be free from
          error, and we are not obliged to notify you of technical
          difficulties. We may modify, suspend or discontinue any part of
          the Site at any time without notice. You agree that we are not
          liable for any loss or damage arising from any such interruption
          or unavailability.
        </p>
      </>
    ),
  },
  {
    id: "no-warranties",
    heading: "No warranties",
    tocLabel: "No warranties",
    body: (
      <>
        <p>
          To the maximum extent permitted by applicable law, the Site and
          all Content are provided "as is" and "as available", without
          warranty or condition of any kind, whether express, implied or
          statutory.
        </p>
        <p>
          Neither Frac nor any of our affiliates or Content Providers makes
          any warranty or representation as to the completeness, security,
          reliability, quality, accuracy, currency or availability of the
          Site or the Content. We expressly disclaim, and you waive, all
          implied warranties and conditions, including any implied warranty
          of merchantability, satisfactory quality, fitness for a
          particular purpose and non-infringement.
        </p>
        <p>
          We do not warrant that errors in the Site or the Content are
          correctable or will be corrected. Any reliance you place on the
          Site or the Content is at your own risk.
        </p>
      </>
    ),
  },
  {
    id: "limitation-of-liability",
    heading: "Limitation of liability",
    tocLabel: "Liability",
    body: (
      <>
        <p>
          To the fullest extent permitted by law, neither Frac nor any of
          our affiliates, officers, employees, agents or Content Providers
          will be liable to you or to any other person for any indirect,
          special, consequential, incidental, exemplary or punitive
          damages, or for any loss of profit, revenue, business,
          anticipated savings, goodwill, reputation or data, or for any
          loss of privacy or security, arising out of or in connection with
          the Site or these Terms, whether in contract, tort (including
          negligence), under statute or otherwise, and whether or not we
          were advised of the possibility of such loss.
        </p>
        <p>
          Where liability cannot be excluded under applicable law, our
          aggregate liability arising out of or in connection with the Site
          and these Terms is limited to one hundred Singapore dollars (SGD
          100).
        </p>
        <p>
          Nothing in these Terms excludes or limits liability for death or
          personal injury caused by negligence, for fraud or fraudulent
          misrepresentation, or for any other liability that cannot
          lawfully be excluded or limited.
        </p>
      </>
    ),
  },
  {
    id: "indemnity",
    heading: "Indemnity",
    tocLabel: "Indemnity",
    body: (
      <p>
        You agree to indemnify, defend and hold harmless Frac, our
        affiliates and our Content Providers from and against all losses,
        damages, liabilities, claims and costs of every nature (including
        reasonable legal fees) arising out of or related to your use of
        the Site, your submission of any information through the Site, or
        any breach or alleged breach by you of these Terms. You agree to
        cooperate fully in the defence of any such claim.
      </p>
    ),
  },
  {
    id: "privacy",
    heading: "Privacy",
    tocLabel: "Privacy",
    body: (
      <p>
        Our collection and handling of personal data through the Site is
        described in our <PrivacyLink />, which forms part of these Terms.
      </p>
    ),
  },
  {
    id: "governing-law-and-jurisdiction",
    heading: "Governing law and jurisdiction",
    tocLabel: "Governing law",
    body: (
      <>
        <p>
          These Terms, and any dispute or claim arising out of or in
          connection with them or their subject matter (including
          non-contractual disputes or claims), are governed by and
          construed in accordance with the laws of the Republic of
          Singapore, without regard to its conflict of laws principles.
        </p>
        <p>
          You and Frac submit to the exclusive jurisdiction of the courts
          of the Republic of Singapore. Any dispute will be resolved solely
          through individual action and will not be brought as a class
          action, collective arbitration or any other representative
          proceeding.
        </p>
      </>
    ),
  },
  {
    id: "general",
    heading: "General",
    tocLabel: "General",
    body: (
      <div className="space-y-5">
        <GeneralClause label="Entire agreement">
          These Terms, together with the Privacy Policy, constitute the
          entire agreement between you and Frac in respect of the Site, and
          supersede all prior understandings relating to it. This clause
          does not affect any separate written agreement between Frac and a
          client in respect of our services.
        </GeneralClause>
        <GeneralClause label="Severability">
          If any provision of these Terms is held to be invalid, illegal
          or unenforceable, that provision will be limited or removed to
          the minimum extent necessary, and the remaining provisions will
          continue in full force and effect.
        </GeneralClause>
        <GeneralClause label="Waiver">
          Our failure to enforce any right or provision is not a waiver of
          that right or provision.
        </GeneralClause>
        <GeneralClause label="Assignment">
          You may not assign or transfer your rights under these Terms. We
          may assign our rights and obligations to any affiliate or to any
          successor in connection with a merger, acquisition or sale of
          assets.
        </GeneralClause>
        <GeneralClause label="Third-party rights">
          A person who is not a party to these Terms has no right under
          the Contracts (Rights of Third Parties) Act 2001 of Singapore to
          enforce any of these Terms, except that our affiliates and
          Content Providers may enforce clauses 15, 16 and 17.
        </GeneralClause>
        <GeneralClause label="Force majeure">
          We are not liable for any failure or delay in performance caused
          by events beyond our reasonable control.
        </GeneralClause>
        <GeneralClause label="Language">
          These Terms are written in English. Any translation is provided
          for convenience only, and the English version prevails.
        </GeneralClause>
      </div>
    ),
  },
  {
    id: "contact-us",
    heading: "Contact us",
    tocLabel: "Contact",
    body: (
      <>
        <p>Questions about these Terms may be sent to:</p>
        <div
          className="grid gap-6 sm:grid-cols-2"
          data-testid="terms-contact-blocks"
        >
          <address className="not-italic">
            <div className="font-semibold text-frac-black">
              Frac Pte. Ltd.
            </div>
            <div className="mt-1 text-frac-gray-70">
              8 Marina View, Asia Square Tower 1, #43-01, Singapore 018960
            </div>
            <div className="mt-2">
              <a
                href="mailto:hello@frac.io"
                className={LINK_CLS}
                data-testid="terms-contact-email"
              >
                hello@frac.io
              </a>
            </div>
          </address>
          <address className="not-italic">
            <div className="font-semibold text-frac-black">
              Frac Sdn. Bhd.
            </div>
            <div className="mt-1 text-frac-gray-70">
              SO-21-1, Menara 1, Strata Office No.3, Jalan Bangsar, KL Eco
              City, 59200 Kuala Lumpur, Malaysia
            </div>
          </address>
        </div>
      </>
    ),
  },
];

export default function Terms() {
  return (
    <>
      <PageSeo
        title="Terms & Conditions — Frac"
        description="Frac Pte. Ltd. Terms & Conditions — the rules governing your use of the frac.io website. Last updated 18 August 2026."
        path="/terms"
        image="/og/terms.png"
      />
      <div data-testid="page-terms">
        <LegalPage
          title="Terms & Conditions"
          sections={sections}
          testId="page-terms-body"
          dateLine="Effective date: 18 August 2026 · Last updated: 18 August 2026"
          draftBanner={false}
          notice={
            <>
              Pending legal review. This document is a working draft
              prepared for review by Frac's external counsel in Singapore
              and Malaysia. It has not yet been approved for reliance.
            </>
          }
        />
      </div>
    </>
  );
}
